import React, {useMemo, useRef, useState, useEffect} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {Box, BoxProps, Divider, DividerProps, GridItem, Text, TextProps, useMultiStyleConfig} from '@chakra-ui/react'
import {useAnalytics, useCustomApi, useHeaderSize, useViewport, useWishlist, useCurrentBasket} from '_customHooks'
import {getAbsoluteUrl, isUserLogged, MetaTags, TILE_ASSET_SIZE} from '_utils'
import {
    Breadcrumb,
    BreadcrumbSkeleton,
    Headline,
    HeadlineSkeleton,
    HideBelowViewport,
    Loader,
    Pagination,
    ProductListing,
    ProductSkeleton,
    ProductTile,
    WishlistButtons,
    Widget,
} from '_uiComponents'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import type {ProductType, WishlistProduct} from '_types'
import {getProductAnalyticsInfo, getProductImpressionAnalytics} from '_analytics/utils/analyticsUtils'
import {useHistory, useLocation} from 'react-router-dom'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'
import {useGlobalConfigState} from '_context'

/**
 * @name Wishlist
 *
 * @page
 *
 * @description
 * Renders the content of Wishlist page.
 *
 * @remarks
 * Content is partially conditionally rendered based on wishlist type (public or private).
 * Pagination is not handled via API, it's just UI on front-end side.
 *
 * @returns {JSX.Element} - Rendered Wishlist page.
 */
const Wishlist = (): JSX.Element => {
    let isFirstLoad = true
    const location = useLocation()
    const history = useHistory()
    const {scrollToTop} = (location.state || {}) as {scrollToTop: boolean}
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const {appPreferences} = useGlobalConfigState()
    const {hostLink, disableEditorialWidget} = appPreferences
    const {locale, site} = useMultiSite()
    const {formatMessage} = useIntl()
    const {isMobile, isTablet} = useViewport()
    const {headerSize} = useHeaderSize()
    const {analyticsEventInit, analyticsEventAddToCart, analyticsEventPwaPage} = useAnalytics()
    const styles = useMultiStyleConfig('Wishlist', {headerSize}) as Record<string, BoxProps & TextProps & DividerProps>
    const hostLinkLocalized = hostLink ? JSON.parse(hostLink)[site.id] : undefined

    const PAGINATION_LIMIT = 12
    const {wishlist, wishlistType, wishlistProductsCount, isWishlistEmpty, isLoading, refetchWishlists} = useWishlist()
    const [shownProductsCount, setShownProductsCount] = useState<number>(PAGINATION_LIMIT)
    const [isActionLoading, setIsActionLoading] = useState<boolean>(false)

    const analyticsList = 'wishlist-guest'

    /**
     * Retrieves product ID.
     *
     * @param {{c_variantData?: ProductType; c_variationGroupsData?: ProductType}} product
     * @returns {string} Product ID.
     */
    const getProductId = (product: {c_variantData?: ProductType; c_variationGroupsData?: Array<ProductType>}): string => {
        return product?.c_variantData?.ID || product?.c_variationGroupsData?.find((variant) => variant.default)?.ID
    }

    /**
     * Mapped products.
     * Since pagination is not handled API-side we're slicing with associated `shownProductsCount` state.
     */
    const products = useMemo(
        () => wishlist?.customerProductListItems?.reverse().map((item) => ({id: getProductId(item.c_productWishlist), ...item.c_productWishlist})) ?? [],
        [wishlist, shownProductsCount],
    ) as Array<WishlistProduct>

    const slicedProducts = useMemo(() => products?.slice(0, shownProductsCount), [products, shownProductsCount])

    const [nextImpressionIndex, setNextImpressionIndex] = useState(0)
    const viewedTiles = useRef(new Set<number>())

    const deviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'

    const tileAssetSize = useMemo(() => {
        return deviceType === 'mobile' ? TILE_ASSET_SIZE[deviceType].full : TILE_ASSET_SIZE[deviceType]['wishlist']
    }, [deviceType])

    const wishlistVariantMapId = useRef<Map<string, string>>(new Map())

    const handleLoadMore = (showItems: number) => {
        setShownProductsCount((prev) => prev + showItems)
    }

    const handleSelectedSize = (productId: string, variantId: string) => {
        wishlistVariantMapId.current.set(productId, variantId)
    }

    /**
     * Triggered after wishlist product item removal.
     */
    const resetProductImpressions = () => {
        viewedTiles.current.clear()
        setNextImpressionIndex(0)
    }

    /**
     * Handles additional behaviour for `buy all products` button.
     * @param {Array<string>} productIds - Array of product IDs.
     */
    const handleBuyAllProducts = (productIds: Array<string>) => {
        const richProducts = products
            .filter((product) => productIds.includes(product.id))
            .map((product, idx) => ({
                ...product,
                position: idx + 1,
            }))

        richProducts.forEach((product) => {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({
                product: product,
                idx: product.position,
                tileAssetSize,
                isFullWidth: isMobile,
                analyticsList: analyticsList,
                flow: 'addToCart',
                page: 'wishlist',
            })
            const {list, ...analyticsProductWithoutList} = analyticsProductObject
            delete analyticsProductWithoutList.dimension14

            analyticsEventAddToCart({
                currencyCode: productCurrency,
                list: list,
                product: analyticsProductWithoutList,
            })
        })

        resetProductImpressions()
    }

    const headlineDescription =
        wishlistProductsCount === 1
            ? formatMessage({id: 'single.item.wishlist', defaultMessage: 'You have {0} item on your Wishlist'}, {0: wishlistProductsCount})
            : formatMessage({id: 'more.items.wishlist', defaultMessage: 'You have {0} items on your Wishlist'}, {0: wishlistProductsCount})

    const metaTags = {
        title: formatMessage({id: 'global.wishlist.title', defaultMessage: 'My Wishlist | Brunello Cucinelli'}),
        description: formatMessage({id: 'global.storename', defaultMessage: 'Brunello Cucinelli - official boutique'}),
    }

    const handleScreenReaderMessage = (shortDescription: string) => {
        const message = formatMessage({id: 'assistive.text.remove.product.wishlist', defaultMessage: '{0} removed from wishlist'}, {0: shortDescription})
        const element = document.querySelector('span.sr-only')
        if (element) {
            element.textContent = message
        }
    }

    /**
     * Initializes GTM data layer.
     */
    useEffect(() => {
        if (customer && locale && basket && isFirstLoad) {
            analyticsEventInit({
                country: locale.id.split('-')[1],
                currency: basket.currency,
                pageCategory: 'wishlist',
                pageSubCategory: '',
                pageDetail: '',
                pageLanguage: locale.id.split('-')[0],
                pageStatus: '200',
                listingType: '',
            })
            isFirstLoad = false
        }
    }, [customer, locale, basket])

    /**
     * Redirect to `/account?tabID=wishlist` route when the customer is authenticated.
     */
    useEffect(() => {
        if (isUserLogged(customer)) {
            window.location.href = getAbsoluteUrl(hostLinkLocalized, locale.alias, '/account?tabID=wishlist')
        }
    }, [customer])

    // Dynamic Yield Recommendation Context for Wishlist page
    useEffect(() => {
        if (typeof window === 'undefined') return

        const dyLocale = locale?.id ? locale.id.replace('-', '_') : undefined
        const context = {
            type: 'Other',
            data: [location.pathname],
            ...(dyLocale ? {lng: dyLocale} : {}),
        }

        window.DY = window.DY || {}
        window.DY.recommendationContext = context
    }, [location.pathname, locale?.id])

    /**
     * Triggers `analyticsEventPwaPage` when data layer is already initialized.
     * @remarks
     * The data layer check ensures that the event will be triggered
     * only between PWA-pages navigation (example: from PLP to wishlist).
     */
    useEffect(() => {
        if (window.dataLayer) {
            analyticsEventPwaPage({
                pageCategory: 'wishlist',
                pagesubCategory: '',
                pageDetail: '',
                pageLanguage: locale.id.split('-')[0],
                pageStatus: '200',
                listingType: '',
            })
        }
    }, [])

    useEffect(() => {
        /**
         * Refetch to avoid issues with private and public product IDs mapping.
         */
        void refetchWishlists()

        /**
         * Reset the history state after page mount.
         */
        history.replace({
            ...location,
            state: null,
        })
    }, [])

    return (
        <>
            <MetaTags {...metaTags} />

            {scrollToTop && <ScrollToTop />}

            {(isLoading || isActionLoading) && <Loader />}

            <Box data-testid="wishlist-page" {...styles.container}>
                <Box className="sr-only" as="span" role="alert" aria-live="polite"></Box>
                <Box {...styles.headline}>
                    {isLoading ? <BreadcrumbSkeleton /> : <Breadcrumb items={wishlist?.c_wishlist?.breadcrumbs} />}

                    {isLoading ? (
                        <HeadlineSkeleton variant="wishlist" hasTitle hasDescription />
                    ) : (
                        <Headline title={<FormattedMessage id="page.wishlist.name" defaultMessage={'My Wishlist'} />} variant="wishlist">
                            {headlineDescription}
                        </Headline>
                    )}

                    {(!isWishlistEmpty || isLoading) && wishlistType === 'private' && (
                        <WishlistButtons
                            wishlistVariantMapId={wishlistVariantMapId.current}
                            products={products}
                            productsCount={wishlistProductsCount}
                            isLoading={isLoading}
                            setIsActionLoading={setIsActionLoading}
                            onWishlistBuyAllProducts={handleBuyAllProducts}
                        />
                    )}

                    {!isLoading && (
                        <HideBelowViewport breakpoint="md">
                            <Divider orientation="horizontal" aria-orientation={undefined} {...styles.divider} />
                        </HideBelowViewport>
                    )}

                    {!isLoading && isWishlistEmpty && (
                        <Text {...styles.emptyWishlistLabel}>
                            <FormattedMessage id="wishlist.empty.text" defaultMessage={'Add products to your Wishlist to see them here.'} />
                        </Text>
                    )}
                </Box>

                {(!isWishlistEmpty || isLoading) && (
                    <ProductListing variant={'wishlist'}>
                        {isLoading
                            ? Array.from({length: 6}, (_, index) => (
                                  <GridItem key={`skeleton-${index}`}>
                                      <ProductSkeleton />
                                  </GridItem>
                              ))
                            : slicedProducts?.map((product, idx) => {
                                  const productId = getProductId(product)
                                  const shortDescription = product.c_variantData?.shortDescription || product.c_variationGroupsData?.[0].shortDescription
                                  return (
                                      <GridItem {...(isMobile ? styles.fullWidthTile : {})} key={product.id}>
                                          <ProductTile
                                              product={{...product, id: productId, productId: productId}}
                                              idx={idx}
                                              listingLayout={'wishlist'}
                                              isFullWidth={isMobile}
                                              nextImpressionIndex={nextImpressionIndex}
                                              setNextImpressionIndex={setNextImpressionIndex}
                                              viewedTiles={viewedTiles}
                                              shouldHideWishlistButton={wishlistType === 'public'}
                                              variant={'wishlist'}
                                              analyticsList={analyticsList}
                                              onUpdateItemSize={(variantId) => handleSelectedSize(product.id, variantId)}
                                              onAddProductToCart={resetProductImpressions}
                                              onWishlistItemRemove={() => {
                                                  resetProductImpressions()
                                                  if (shortDescription) {
                                                      handleScreenReaderMessage(shortDescription)
                                                  }
                                              }}
                                          />
                                      </GridItem>
                                  )
                              })}
                        <Pagination
                            start={1}
                            end={slicedProducts.length}
                            total={wishlistProductsCount}
                            limit={PAGINATION_LIMIT}
                            onLoadMore={handleLoadMore}
                            isLoading={isLoading}
                        />
                    </ProductListing>
                )}
            </Box>

            {(disableEditorialWidget === null ||
                (typeof disableEditorialWidget !== 'undefined' &&
                    disableEditorialWidget !== null &&
                    disableEditorialWidget.indexOf('WISHLISTGUEST') === -1)) && <Widget />}
        </>
    )
}

Wishlist.getTemplateName = () => 'wishlist'

export default Wishlist
